/**
 * CFR Violation Data
 * Common CFR violations with severity weights for BASIC score calculations
 *
 * Data source: FMCSA CFR Severity Weight table
 * Used for What-If scenario analysis
 */

export interface CfrViolation {
  code: string;
  description: string;
  basicCategory: string;
  severityWeight: number;
}

/**
 * Top 50 most common CFR violations across all BASIC categories
 * Organized by BASIC category for easy selection
 */
export const CFR_VIOLATIONS: CfrViolation[] = [
  // BASIC-11: Unsafe Driving
  { code: '392.2', description: 'Operating CMV while texting', basicCategory: 'BASIC-11', severityWeight: 10.0 },
  { code: '392.2-SLLS2', description: 'Failure to use seat belt', basicCategory: 'BASIC-11', severityWeight: 7.0 },
  { code: '392.16', description: 'Failing to use seat belt', basicCategory: 'BASIC-11', severityWeight: 7.0 },
  { code: '392.10', description: 'Speeding 15+ mph over limit', basicCategory: 'BASIC-11', severityWeight: 10.0 },
  { code: '392.80(a)', description: 'Texting while driving', basicCategory: 'BASIC-11', severityWeight: 10.0 },
  { code: '392.2C', description: 'Using handheld mobile phone', basicCategory: 'BASIC-11', severityWeight: 10.0 },
  { code: '392.14', description: 'Failure to use caution - hazardous conditions', basicCategory: 'BASIC-11', severityWeight: 5.0 },
  { code: '392.71', description: 'Radar detector - using or possessing', basicCategory: 'BASIC-11', severityWeight: 5.0 },
  { code: '392.2-SLLTCD', description: 'Speeding 6-10 mph over limit', basicCategory: 'BASIC-11', severityWeight: 4.0 },
  { code: '392.2-SLLS', description: 'State/local laws - speeding', basicCategory: 'BASIC-11', severityWeight: 7.0 },
  { code: '392.2WC', description: 'Improper lane change', basicCategory: 'BASIC-11', severityWeight: 5.0 },
  { code: '392.2-SLLS3', description: 'Speeding 11-14 mph over limit', basicCategory: 'BASIC-11', severityWeight: 7.0 },
  { code: '392.2FC', description: 'Following too closely', basicCategory: 'BASIC-11', severityWeight: 5.0 },
  { code: '392.22(a)', description: 'Failing to use hazard warning flashers', basicCategory: 'BASIC-11', severityWeight: 3.0 },

  // BASIC-12: Hours of Service
  { code: '395.8(a)', description: 'Log violation (general)', basicCategory: 'BASIC-12', severityWeight: 5.0 },
  { code: '395.3(a)(3)(ii)', description: 'Driving beyond 8-hour limit without break', basicCategory: 'BASIC-12', severityWeight: 7.0 },
  { code: '395.11(a)', description: 'ELD general violation', basicCategory: 'BASIC-12', severityWeight: 5.0 },
  { code: '395.8(k)(1)', description: 'Form and manner violation', basicCategory: 'BASIC-12', severityWeight: 3.0 },
  { code: '395.8(k)(2)', description: 'False record of duty status', basicCategory: 'BASIC-12', severityWeight: 7.0 },
  { code: '395.8(e)', description: 'Record of duty status not current', basicCategory: 'BASIC-12', severityWeight: 5.0 },
  { code: '395.3(a)(1)', description: 'Driving more than 11 hours', basicCategory: 'BASIC-12', severityWeight: 7.0 },
  { code: '395.3(a)(2)', description: 'Driving beyond 14-hour limit', basicCategory: 'BASIC-12', severityWeight: 7.0 },
  { code: '395.11(h)', description: 'ELD malfunction - failed to note', basicCategory: 'BASIC-12', severityWeight: 5.0 },
  { code: '395.22(h)', description: 'No supporting documents for ELD', basicCategory: 'BASIC-12', severityWeight: 3.0 },
  { code: '395.8(a)(1)', description: 'No record of duty status', basicCategory: 'BASIC-12', severityWeight: 7.0 },
  { code: '395.30(b)', description: 'No ELD user manual', basicCategory: 'BASIC-12', severityWeight: 1.0 },

  // BASIC-13: Driver Fitness
  { code: '391.15(a)', description: 'Operating without valid CDL', basicCategory: 'BASIC-13', severityWeight: 8.0 },
  { code: '391.41(a)(1)', description: 'Operating without medical certificate', basicCategory: 'BASIC-13', severityWeight: 5.0 },
  { code: '391.11(b)(2)', description: 'Driver not qualified', basicCategory: 'BASIC-13', severityWeight: 8.0 },
  { code: '391.45(a)', description: 'No medical certificate in possession', basicCategory: 'BASIC-13', severityWeight: 1.0 },
  { code: '391.45(b)', description: 'Expired medical certificate', basicCategory: 'BASIC-13', severityWeight: 5.0 },
  { code: '391.15(a)(1)', description: 'No CDL', basicCategory: 'BASIC-13', severityWeight: 8.0 },
  { code: '391.41(a)', description: 'Not medically qualified', basicCategory: 'BASIC-13', severityWeight: 8.0 },
  { code: '383.23(a)(2)', description: 'Operating CMV without proper endorsement', basicCategory: 'BASIC-13', severityWeight: 5.0 },
  { code: '391.11(a)', description: 'Failing to have valid operator license', basicCategory: 'BASIC-13', severityWeight: 8.0 },
  { code: '383.51', description: 'CDL suspended/revoked', basicCategory: 'BASIC-13', severityWeight: 8.0 },

  // BASIC-15: Vehicle Maintenance
  { code: '393.75(a)(3)', description: 'Tire - flat or audible air leak', basicCategory: 'BASIC-15', severityWeight: 8.0 },
  { code: '393.75A3-TAOL', description: 'Tire - flat or audible air leak (OOS)', basicCategory: 'BASIC-15', severityWeight: 8.0 },
  { code: '393.9', description: 'Inoperative brake lamps', basicCategory: 'BASIC-15', severityWeight: 6.0 },
  { code: '393.45', description: 'Brake defects (general)', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '393.55(e)', description: 'ABS malfunction indicators', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '396.3(a)(1)', description: 'Inspection, repair, maintenance', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '396.17C-PI', description: 'Operating vehicle without periodic inspection', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '393.75(a)', description: 'Tire - ply or belt exposed', basicCategory: 'BASIC-15', severityWeight: 8.0 },
  { code: '393.47(a)', description: 'Brake out of adjustment', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '393.47E', description: 'Brakes - 20% or more defective brakes', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '393.9(a)', description: 'Inoperative required lamps', basicCategory: 'BASIC-15', severityWeight: 6.0 },
  { code: '396.3(a)', description: 'Parts/accessories - defective', basicCategory: 'BASIC-15', severityWeight: 2.0 },
  { code: '393.95(a)', description: 'No/discharged/unsecured fire extinguisher', basicCategory: 'BASIC-15', severityWeight: 2.0 },
  { code: '393.53(b)', description: 'CMV manufactured after 10/19/94 has automatic brake adjustment system that fails to compensate for worn brake linings', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '393.43(d)', description: 'No or defective breakaway brake', basicCategory: 'BASIC-15', severityWeight: 4.0 },
  { code: '393.11', description: 'No or defective lighting devices', basicCategory: 'BASIC-15', severityWeight: 6.0 },

  // BASIC-12: Hours of Service - Additional codes
  { code: '395.8E-HOSPD', description: 'HOS (property) - record of duty status violation', basicCategory: 'BASIC-12', severityWeight: 5.0 },

  // BASIC-11: Unsafe Driving - Additional codes
  { code: '392.2-SLL', description: 'State/Local Laws - Various traffic violations', basicCategory: 'BASIC-11', severityWeight: 7.0 },
  { code: '392.2-SLLSR', description: 'State/Local Laws - Speeding related', basicCategory: 'BASIC-11', severityWeight: 7.0 },
  { code: '392.2-SLLLR', description: 'State/Local Laws - Lane/traffic violations', basicCategory: 'BASIC-11', severityWeight: 5.0 },
];

/**
 * Get violations by BASIC category
 */
export const getViolationsByBasic = (basicId: string): CfrViolation[] => {
  return CFR_VIOLATIONS.filter(v => v.basicCategory === basicId);
};

/**
 * Get violation by CFR code
 */
export const getViolationByCode = (code: string): CfrViolation | undefined => {
  return CFR_VIOLATIONS.find(v => v.code === code);
};

/**
 * Get all unique BASIC categories
 */
export const getBasicCategories = (): string[] => {
  return Array.from(new Set(CFR_VIOLATIONS.map(v => v.basicCategory))).sort();
};
